import {
  NATIVE_CONVERSATION_THEMES,
  fingerprintPrivateSummary,
  safeExternalURL,
  type AudienceAssignment,
  type Bookmark,
  type LibraryFormat,
  type LibraryLane,
  type LibraryPublication,
} from "./domain";

export const NEW_CARD_DRAFT_SCHEMA_VERSION = 1 as const;
export const NEW_CARD_PHOTO_SCHEMA_VERSION = 1 as const;
export const NEW_CARD_DRAFT_ID_PREFIX = "local-new-card-";
export const NEW_CARD_MEDIA_REFERENCE_PREFIX = "new-card-media:";
export const MAX_NEW_CARD_MEDIA_BYTES = 15 * 1024 * 1024;
export const MAX_NEW_CARD_TITLE_LENGTH = 500;
export const MAX_NEW_CARD_SOURCE_LABEL_LENGTH = 200;
export const MAX_NEW_CARD_PRIVATE_NOTES_LENGTH = 10_000;
export const MAX_NEW_CARD_PRIVATE_SUMMARY_LENGTH = 5_000;
export const MAX_NEW_CARD_SHARED_NOTES_LENGTH = 5_000;
export const MAX_NEW_CARD_TERM_LENGTH = 100;
export const MAX_NEW_CARD_TERMS = 24;

const libraryFormats = new Set<LibraryFormat>([
  "text",
  "audio",
  "video",
  "unknown",
]);
const libraryLanes = new Set<LibraryLane>(["read", "listen", "watch"]);

export type NewCardSourceKind = "link" | "text" | "photo";

export interface NewCardPhotoMetadata {
  schemaVersion: typeof NEW_CARD_PHOTO_SCHEMA_VERSION;
  reference: string;
  mimeType: string;
  byteCount: number;
  sha256: string;
}

export interface NewCardAudience {
  id: string;
  label: string;
}

export interface NewCardPlacement {
  audienceId: string;
  audienceLabel: string;
  conversationEnabled: boolean;
  libraryEnabled: boolean;
  isPrimary: boolean;
  sharedTitle: string;
  sharedSourceLabel: string;
  sharedURL: string;
  sharedNotes: string;
}

export interface NewCardDraft {
  schemaVersion: typeof NEW_CARD_DRAFT_SCHEMA_VERSION;
  id: string;
  createdAt: string;
  updatedAt: string;
  sourceKind: NewCardSourceKind;
  photo?: NewCardPhotoMetadata | null;
  ownerTitle: string;
  sourceLabel: string;
  sourceURL: string;
  primaryTheme: string;
  privateNotes: string;
  privateHumanSummary: string;
  keywords: string[];
  tags: string[];
  format: LibraryFormat;
  lane: LibraryLane;
  placements: NewCardPlacement[];
}

export interface NewCardValidationError {
  field:
    | "source"
    | "photo"
    | "title"
    | "theme"
    | "privateNotes"
    | "privateHumanSummary"
    | "keywords"
    | "tags"
    | "placements"
    | `placement:${string}`;
  message: string;
}

export function createNewCardDraft(
  id: string,
  audiences: readonly NewCardAudience[],
  now = new Date().toISOString(),
): NewCardDraft {
  const draftID = id.trim();
  if (!draftID.startsWith(NEW_CARD_DRAFT_ID_PREFIX)) {
    throw new Error("A private New Card draft needs a local draft identity.");
  }
  if (!validISODate(now)) {
    throw new Error("A private New Card draft needs a valid creation time.");
  }
  assertAvailableAudiences(audiences);
  return {
    schemaVersion: NEW_CARD_DRAFT_SCHEMA_VERSION,
    id: draftID,
    createdAt: now,
    updatedAt: now,
    sourceKind: "link",
    photo: null,
    ownerTitle: "",
    sourceLabel: "",
    sourceURL: "",
    primaryTheme: "interesting",
    privateNotes: "",
    privateHumanSummary: "",
    keywords: [],
    tags: [],
    format: "unknown",
    lane: "read",
    placements: audiences.map(({ id: audienceId, label: audienceLabel }) => ({
      audienceId,
      audienceLabel,
      conversationEnabled: false,
      libraryEnabled: false,
      isPrimary: false,
      sharedTitle: "",
      sharedSourceLabel: "",
      sharedURL: "",
      sharedNotes: "",
    })),
  };
}

export function seedNewCardSource(
  draft: NewCardDraft,
  sourceKind: Exclude<NewCardSourceKind, "photo">,
  input: string,
  now = new Date().toISOString(),
): NewCardDraft {
  const normalizedInput = input.trim().replace(/\s+/g, " ");
  if (sourceKind === "link") {
    const sourceURL = safeExternalURL(normalizedInput);
    if (!sourceURL) {
      throw new Error("Paste a full http:// or https:// link.");
    }
    const host = new URL(sourceURL).hostname.replace(/^www\./i, "");
    return {
      ...structuredClone(draft),
      sourceKind,
      photo: null,
      sourceURL,
      ownerTitle: draft.ownerTitle.trim() || host,
      sourceLabel: draft.sourceLabel.trim() || host,
      updatedAt: now,
    };
  }
  if (!normalizedInput) {
    throw new Error("Write the thought you want to keep.");
  }
  return {
    ...structuredClone(draft),
    sourceKind,
    photo: null,
    ownerTitle: normalizedInput,
    sourceLabel: draft.sourceLabel.trim(),
    sourceURL: "",
    privateNotes: draft.privateNotes.trim() || normalizedInput,
    format: "text",
    lane: "read",
    updatedAt: now,
  };
}

export function seedNewCardPhoto(
  draft: NewCardDraft,
  photo: NewCardPhotoMetadata,
  now = new Date().toISOString(),
): NewCardDraft {
  if (!isNewCardPhotoMetadata(photo)) {
    throw new Error("Choose a valid private photo.");
  }
  return {
    ...structuredClone(draft),
    sourceKind: "photo",
    photo: structuredClone(photo),
    ownerTitle: draft.ownerTitle.trim() || "Photo",
    sourceLabel: "",
    sourceURL: "",
    format: "unknown",
    lane: "read",
    updatedAt: now,
  };
}

export function checkpointNewCardDraft(
  previous: NewCardDraft,
  next: NewCardDraft,
  now = new Date().toISOString(),
): NewCardDraft {
  if (
    previous.id !== next.id
    || previous.createdAt !== next.createdAt
    || previous.schemaVersion !== next.schemaVersion
  ) {
    throw new Error("A New Card checkpoint cannot change draft identity.");
  }
  const previousContent = {
    ...previous,
    updatedAt: "",
  };
  const nextContent = {
    ...next,
    updatedAt: "",
  };
  if (JSON.stringify(previousContent) === JSON.stringify(nextContent)) {
    return {
      ...structuredClone(next),
      updatedAt: previous.updatedAt,
    };
  }
  if (!validISODate(now)) {
    throw new Error("A changed New Card draft needs a valid update time.");
  }
  return {
    ...structuredClone(next),
    updatedAt: now,
  };
}

export function validateNewCardDraft(
  draft: NewCardDraft,
  availableAudiences: readonly NewCardAudience[],
): NewCardValidationError | null {
  if (!isNewCardDraft(draft)) {
    return {
      field: "source",
      message: "This New Card draft is invalid. Discard it and start again.",
    };
  }
  const title = draft.ownerTitle.trim();
  if (!title) {
    return {
      field: "title",
      message: "Add a private card title before saving.",
    };
  }
  if (title.length > MAX_NEW_CARD_TITLE_LENGTH) {
    return {
      field: "title",
      message: `Keep the private title to ${MAX_NEW_CARD_TITLE_LENGTH} characters or fewer.`,
    };
  }
  if (
    draft.sourceLabel.trim().length > MAX_NEW_CARD_SOURCE_LABEL_LENGTH
  ) {
    return {
      field: "source",
      message: `Keep the source label to ${MAX_NEW_CARD_SOURCE_LABEL_LENGTH} characters or fewer.`,
    };
  }
  if (draft.sourceKind === "link" && !safeExternalURL(draft.sourceURL)) {
    return {
      field: "source",
      message: "Paste a full http:// or https:// link.",
    };
  }
  if (
    (draft.sourceKind === "text" || draft.sourceKind === "photo")
    && draft.sourceURL.trim()
  ) {
    return {
      field: "source",
      message: `${
        draft.sourceKind === "photo" ? "A photo" : "A text-only card"
      } cannot silently keep a source link.`,
    };
  }
  if (draft.sourceKind === "photo" && !isNewCardPhotoMetadata(draft.photo)) {
    return {
      field: "photo",
      message: "Choose a private photo before saving this card.",
    };
  }
  if (
    draft.sourceKind !== "photo"
    && draft.photo !== undefined
    && draft.photo !== null
  ) {
    return {
      field: "photo",
      message: "Remove the private photo or change this card back to Photo.",
    };
  }
  if (!NATIVE_CONVERSATION_THEMES.includes(
    draft.primaryTheme as (typeof NATIVE_CONVERSATION_THEMES)[number],
  )) {
    return {
      field: "theme",
      message: "Choose one of Convoful’s Conversation themes.",
    };
  }
  if (draft.privateNotes.trim().length > MAX_NEW_CARD_PRIVATE_NOTES_LENGTH) {
    return {
      field: "privateNotes",
      message: `Keep private Notes to ${MAX_NEW_CARD_PRIVATE_NOTES_LENGTH} characters or fewer.`,
    };
  }
  if (!draft.privateHumanSummary.trim()) {
    return {
      field: "privateHumanSummary",
      message: "Add a private Summary or use Notes as starter Summary.",
    };
  }
  if (
    draft.privateHumanSummary.trim().length
    > MAX_NEW_CARD_PRIVATE_SUMMARY_LENGTH
  ) {
    return {
      field: "privateHumanSummary",
      message: `Keep the private Summary to ${MAX_NEW_CARD_PRIVATE_SUMMARY_LENGTH} characters or fewer.`,
    };
  }
  const keywordError = validateTerms(draft.keywords, "keywords");
  if (keywordError) return keywordError;
  const tagError = validateTerms(draft.tags, "tags");
  if (tagError) return tagError;

  const availableByID = new Map(
    availableAudiences.map(({ id, label }) => [id, label]),
  );
  const seen = new Set<string>();
  for (const placement of draft.placements) {
    if (seen.has(placement.audienceId)) {
      return {
        field: "placements",
        message: `Audience ${placement.audienceLabel} appears more than once.`,
      };
    }
    seen.add(placement.audienceId);
    if (!availableByID.has(placement.audienceId)) {
      return {
        field: `placement:${placement.audienceId}`,
        message: `${placement.audienceLabel} is no longer available. Choose the current audience placement before saving.`,
      };
    }
    if (
      placement.libraryEnabled
      && !placement.sharedTitle.trim()
    ) {
      return {
        field: `placement:${placement.audienceId}`,
        message: `Add the Shared Library title for ${placement.audienceLabel}.`,
      };
    }
    if (
      placement.libraryEnabled
      && !placement.sharedSourceLabel.trim()
    ) {
      return {
        field: `placement:${placement.audienceId}`,
        message: `Add the shared source label for ${placement.audienceLabel}.`,
      };
    }
    if (
      placement.libraryEnabled
      && placement.sharedURL.trim()
      && !safeExternalURL(placement.sharedURL)
    ) {
      return {
        field: `placement:${placement.audienceId}`,
        message: `Enter a full http:// or https:// Shared Library link for ${placement.audienceLabel}, or leave it blank.`,
      };
    }
    if (
      placement.sharedNotes.trim().length > MAX_NEW_CARD_SHARED_NOTES_LENGTH
    ) {
      return {
        field: `placement:${placement.audienceId}`,
        message: `Keep Shared Notes for ${placement.audienceLabel} to ${MAX_NEW_CARD_SHARED_NOTES_LENGTH} characters or fewer.`,
      };
    }
  }
  const retained = draft.placements.filter(({ conversationEnabled, libraryEnabled }) => (
    conversationEnabled || libraryEnabled
  ));
  if (!retained.length) {
    return {
      field: "placements",
      message: "Choose at least one Private card or Shared Library destination.",
    };
  }
  const primaries = retained.filter(({ isPrimary }) => isPrimary);
  if (primaries.length !== 1) {
    return {
      field: "placements",
      message: "Choose exactly one default audience from the enabled destinations.",
    };
  }
  return null;
}

export function newCardBookmark(
  draft: NewCardDraft,
  ownerUserID: string,
  availableAudiences: readonly NewCardAudience[],
): Bookmark {
  const error = validateNewCardDraft(draft, availableAudiences);
  if (error) throw new Error(error.message);
  const ownerID = ownerUserID.trim();
  if (!ownerID) throw new Error("A New Card needs an active owner.");
  const retained = draft.placements.filter(({ conversationEnabled, libraryEnabled }) => (
    conversationEnabled || libraryEnabled
  ));
  const assignments: AudienceAssignment[] = retained.map((placement) => ({
    id: localOpaqueID("assignment", draft.id, placement.audienceId),
    audienceId: placement.audienceId,
    audienceLabel: placement.audienceLabel,
    conversationEnabled: placement.conversationEnabled,
    libraryEnabled: placement.libraryEnabled,
    isPrimary: placement.isPrimary,
    conversationState: "available",
  }));
  const assignmentByAudience = new Map(
    assignments.map((assignment) => [assignment.audienceId, assignment]),
  );
  const publications: LibraryPublication[] = retained.flatMap((placement) => {
    if (!placement.libraryEnabled) return [];
    const assignment = assignmentByAudience.get(placement.audienceId);
    if (!assignment) return [];
    return [{
      id: localOpaqueID("publication", draft.id, placement.audienceId),
      assignmentId: assignment.id,
      sharedByUserID: ownerID,
      sharedTitle: placement.sharedTitle.trim(),
      sharedURL: safeExternalURL(placement.sharedURL),
      sharedSourceLabel: placement.sharedSourceLabel.trim(),
      sharedNotes: placement.sharedNotes.trim(),
      publishedAt: draft.createdAt,
      updatedAt: draft.updatedAt,
      revokedAt: null,
      format: draft.format,
      lane: draft.lane,
    }];
  });
  const privateHumanSummary = draft.privateHumanSummary.trim();
  return {
    id: draft.id,
    title: draft.ownerTitle.trim(),
    sourceLabel: draft.sourceLabel.trim(),
    sourceURL: safeExternalURL(draft.sourceURL) ?? "",
    senderID: ownerID,
    senderLabel: "You",
    audienceAssignments: assignments,
    libraryPublications: publications,
    format: draft.format,
    lane: draft.lane,
    createdAt: draft.createdAt,
    primaryTheme: draft.primaryTheme,
    privateCardMedia: draft.sourceKind === "photo" && draft.photo
      ? [{
        kind: "image",
        url: draft.photo.reference,
        alt: draft.ownerTitle.trim() || "Photo",
      }]
      : [],
    privateNotes: draft.privateNotes.trim(),
    privateHumanSummary,
    privateSummaryInputMode: privateHumanSummary ? "typed" : null,
    triageAudit: null,
    summaryFingerprint: fingerprintPrivateSummary(privateHumanSummary),
    embeddedSummaryFingerprint: null,
    vectorStatus: "pending",
    keywords: normalizedTerms(draft.keywords),
    tags: normalizedTerms(draft.tags),
  };
}

export function isNewCardDraft(value: unknown): value is NewCardDraft {
  if (!isRecord(value)) return false;
  if (
    value.schemaVersion !== NEW_CARD_DRAFT_SCHEMA_VERSION
    || typeof value.id !== "string"
    || !value.id.startsWith(NEW_CARD_DRAFT_ID_PREFIX)
    || typeof value.createdAt !== "string"
    || !validISODate(value.createdAt)
    || typeof value.updatedAt !== "string"
    || !validISODate(value.updatedAt)
    || (
      value.sourceKind !== "link"
      && value.sourceKind !== "text"
      && value.sourceKind !== "photo"
    )
    || (
      "photo" in value
      && value.photo !== null
      && !isNewCardPhotoMetadata(value.photo)
    )
    || typeof value.ownerTitle !== "string"
    || typeof value.sourceLabel !== "string"
    || typeof value.sourceURL !== "string"
    || typeof value.primaryTheme !== "string"
    || typeof value.privateNotes !== "string"
    || typeof value.privateHumanSummary !== "string"
    || !isStringArray(value.keywords)
    || !isStringArray(value.tags)
    || typeof value.format !== "string"
    || !libraryFormats.has(value.format as LibraryFormat)
    || typeof value.lane !== "string"
    || !libraryLanes.has(value.lane as LibraryLane)
    || !Array.isArray(value.placements)
  ) {
    return false;
  }
  return value.placements.every(isNewCardPlacement);
}

export function normalizeNewCardDraft(draft: NewCardDraft): NewCardDraft {
  return {
    ...structuredClone(draft),
    photo: draft.photo ? structuredClone(draft.photo) : null,
  };
}

export function isNewCardPhotoMetadata(
  value: unknown,
): value is NewCardPhotoMetadata {
  return isRecord(value)
    && value.schemaVersion === NEW_CARD_PHOTO_SCHEMA_VERSION
    && typeof value.reference === "string"
    && new RegExp(
      `^${NEW_CARD_MEDIA_REFERENCE_PREFIX}[a-z0-9][a-z0-9-]{0,127}$`,
      "i",
    ).test(value.reference)
    && typeof value.mimeType === "string"
    && Boolean(value.mimeType.trim())
    && typeof value.byteCount === "number"
    && Number.isInteger(value.byteCount)
    && value.byteCount > 0
    && value.byteCount <= MAX_NEW_CARD_MEDIA_BYTES
    && typeof value.sha256 === "string"
    && /^[a-f0-9]{64}$/.test(value.sha256);
}

function validateTerms(
  terms: string[],
  field: "keywords" | "tags",
): NewCardValidationError | null {
  if (terms.length > MAX_NEW_CARD_TERMS) {
    return {
      field,
      message: `Keep ${field} to ${MAX_NEW_CARD_TERMS} entries or fewer.`,
    };
  }
  if (terms.some((term) => term.trim().length > MAX_NEW_CARD_TERM_LENGTH)) {
    return {
      field,
      message: `Keep every ${field === "keywords" ? "keyword" : "tag"} to ${MAX_NEW_CARD_TERM_LENGTH} characters or fewer.`,
    };
  }
  return null;
}

function normalizedTerms(terms: string[]): string[] {
  const seen = new Set<string>();
  return terms.flatMap((term) => {
    const normalized = term.trim();
    const key = normalized.toLocaleLowerCase();
    if (!normalized || seen.has(key)) return [];
    seen.add(key);
    return [normalized];
  });
}

function localOpaqueID(
  kind: "assignment" | "publication",
  draftID: string,
  audienceID: string,
): string {
  let hash = 0x811c9dc5;
  const value = `${kind}\n${draftID}\n${audienceID}`;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `local-${kind}-${(hash >>> 0).toString(36).padStart(7, "0")}`;
}

function assertAvailableAudiences(
  audiences: readonly NewCardAudience[],
): void {
  const seen = new Set<string>();
  for (const audience of audiences) {
    if (!audience.id.trim() || !audience.label.trim()) {
      throw new Error("Every New Card audience needs an identity and label.");
    }
    if (seen.has(audience.id)) {
      throw new Error("New Card audiences must be unique.");
    }
    seen.add(audience.id);
  }
}

function isNewCardPlacement(value: unknown): value is NewCardPlacement {
  return isRecord(value)
    && typeof value.audienceId === "string"
    && Boolean(value.audienceId.trim())
    && typeof value.audienceLabel === "string"
    && Boolean(value.audienceLabel.trim())
    && typeof value.conversationEnabled === "boolean"
    && typeof value.libraryEnabled === "boolean"
    && typeof value.isPrimary === "boolean"
    && typeof value.sharedTitle === "string"
    && typeof value.sharedSourceLabel === "string"
    && typeof value.sharedURL === "string"
    && typeof value.sharedNotes === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function validISODate(value: string): boolean {
  return Boolean(value)
    && !Number.isNaN(new Date(value).valueOf());
}
